import { CheckCircle2, Cloud, Database, Download, ShieldCheck, Trash2, Upload, X } from 'lucide-react';
import { useReducer, useRef } from 'react';
import type { AppCapabilities } from '../hooks/useAppCapabilities';
import type { InstallPromptController } from '../hooks/useInstallPrompt';
import type { PlaceKind } from '../services/items';
import { downloadBackup, restoreBackup } from '../services/backup';
import type { ItemRecord } from '../types';
import { Modal } from './Modal';
import { PlaceManager } from './PlaceManager';

type Props = {
  capabilities: AppCapabilities;
  install: InstallPromptController;
  items: ItemRecord[];
  onClose: () => void;
  onDataRestored: () => void;
  onClearData: () => Promise<void>;
  onRenamePlace: (kind: PlaceKind, previousName: string, nextName: string) => Promise<number>;
};

export function SettingsDialog({ capabilities, install, items, onClose, onDataRestored, onClearData, onRenamePlace }: Props) {
  const backupInputRef = useRef<HTMLInputElement>(null);
  const [state, updateState] = useReducer(
    (current: SettingsState, patch: Partial<SettingsState>) => ({ ...current, ...patch }),
    { message: '', backupBusy: false }
  );

  const clearData = async () => {
    if (!confirm('Delete every item, photo, pending sync change, and local setting from this browser?')) return;
    updateState({ backupBusy: true });
    try {
      await onClearData();
      onClose();
    } catch (error) {
      updateState({ message: error instanceof Error ? error.message : 'Local data could not be deleted.' });
    } finally {
      updateState({ backupBusy: false });
    }
  };

  const download = async () => {
    updateState({ backupBusy: true });
    try {
      const count = await downloadBackup();
      updateState({ message: `Backup downloaded with ${count} item${count === 1 ? '' : 's'}. Keep it private; it includes photos.` });
    } catch (error) {
      updateState({ message: error instanceof Error ? error.message : 'The backup could not be created.' });
    } finally {
      updateState({ backupBusy: false });
    }
  };

  const restore = async (file: File | undefined) => {
    if (!file) return;
    updateState({ backupBusy: true });
    try {
      const count = await restoreBackup(file);
      updateState({ message: count ? `Restored ${count} item${count === 1 ? '' : 's'}. Household sync will run automatically.` : 'Everything in this backup is already on this device.' });
      onDataRestored();
    } catch (error) {
      updateState({ message: error instanceof Error ? error.message : 'The backup could not be restored.' });
    } finally {
      updateState({ backupBusy: false });
      if (backupInputRef.current) backupInputRef.current.value = '';
    }
  };

  return (
    <Modal
      className="dialog settings-dialog"
      ariaLabelledBy="settings-title"
      onClose={onClose}
    >
        <header><div><h2 id="settings-title">App settings</h2><p>This device is ready. Family sync happens automatically.</p></div><button className="icon-button quiet" type="button" aria-label="Close settings" onClick={onClose}><X size={21} /></button></header>

        <div className="settings-section">
          <h3><Database size={19} />This device</h3>
          <div className="settings-status-grid">
            <span><CheckCircle2 size={18} />Local database ready</span>
            <span><ShieldCheck size={18} />{capabilities.storagePersisted ? 'Durable storage granted' : 'Install for stronger storage durability'}</span>
            <span><Download size={18} />{formatStorage(capabilities.storageUsage)} used locally</span>
            <span><Cloud size={18} />Family sync ready</span>
          </div>
          <p>The web app includes the family sync key so a new device works without setup. Treat the app address as household-only; browsers cannot hide an embedded key like native secure storage can.</p>
        </div>

        <div className="settings-section">
          <h3><Download size={19} />Install this app</h3>
          {install.installed ? (
            <p>Household Items is installed and ready from your Home Screen.</p>
          ) : install.isIos ? (
            <div className="install-settings">
              <p>In Safari, tap Share, then <strong>Add to Home Screen</strong>. Installation improves storage durability and keeps the app one tap away.</p>
            </div>
          ) : (
            <div className="install-settings">
              <p>Install Household Items for quicker access and stronger offline storage.</p>
              {install.canPrompt ? <button className="button secondary" type="button" onClick={() => void install.promptInstall()}><Download size={18} />Install app</button> : <p>Use your browser menu and choose <strong>Install app</strong>.</p>}
            </div>
          )}
        </div>

        <div className="settings-section">
          <PlaceManager items={items} onRename={onRenamePlace} />
        </div>

        <div className="settings-section">
          <h3><Download size={19} />Backup and restore</h3>
          <p>Sync keeps family devices together automatically. A downloaded backup is an extra recovery copy containing item details and photos.</p>
          <div className="settings-actions backup-actions">
            <button className="button secondary" type="button" onClick={() => void download()} disabled={state.backupBusy}><Download size={18} />Download backup</button>
            <button className="button secondary" type="button" onClick={() => backupInputRef.current?.click()} disabled={state.backupBusy}><Upload size={18} />Restore backup</button>
            <input ref={backupInputRef} className="visually-hidden" type="file" accept="application/json,.json" aria-label="Choose a Household Items backup" onChange={(event) => void restore(event.target.files?.[0])} />
          </div>
          {state.message ? <p className="settings-message" aria-live="polite">{state.message}</p> : null}
        </div>

        <div className="settings-section danger-zone">
          <div><h3>Reset this device</h3><p>Deletes local items from this browser. Shared household items will return automatically from family sync.</p></div>
          <button className="button danger" type="button" onClick={() => void clearData()} disabled={state.backupBusy}><Trash2 size={18} />Delete local data</button>
        </div>
    </Modal>
  );
}

type SettingsState = {
  message: string;
  backupBusy: boolean;
};

function formatStorage(bytes: number) {
  if (!bytes) return 'Less than 1 MB';
  const megabytes = bytes / 1024 / 1024;
  return `${megabytes < 1 ? '<1' : megabytes.toFixed(megabytes < 10 ? 1 : 0)} MB`;
}
